import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Download, FileText, FileSpreadsheet } from 'lucide-react';

const DownloadDialog = ({
  downloadDialog, setDownloadDialog,
  dlFilterType, setDlFilterType,
  dlFormat, setDlFormat,
  dlPayDateStart, setDlPayDateStart,
  dlPayDateEnd, setDlPayDateEnd,
  selectedBillsSize, activeFY,
  executeDownload,
}) => {
  return (
    <Dialog open={downloadDialog} onOpenChange={v => { if (!v) setDownloadDialog(false); }}>
      <DialogContent className="max-w-md z-50">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Download className="w-5 h-5" /> Download Commission Report</DialogTitle>
          <DialogDescription>Choose what to include and the format</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 pt-2">
          {/* Filter Type */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Report Type</Label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: 'all', label: 'Full Report', desc: `All bills in ${activeFY.label}` },
                { key: 'paid', label: 'Paid Only', desc: 'Filter by payment date' },
                { key: 'unpaid', label: 'Unpaid Statement', desc: 'Only pending bills' },
                { key: 'custom', label: 'Selected Bills', desc: `${selectedBillsSize} bill(s) selected` },
              ].map(opt => (
                <button key={opt.key} onClick={() => setDlFilterType(opt.key)}
                  disabled={opt.key === 'custom' && selectedBillsSize === 0}
                  className={`text-left p-3 rounded-lg border-2 transition-all ${dlFilterType === opt.key ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'} ${opt.key === 'custom' && selectedBillsSize === 0 ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                  data-testid={`dl-filter-${opt.key}`}>
                  <div className="font-medium text-sm">{opt.label}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Payment Date Range (only for Paid filter) */}
          {dlFilterType === 'paid' && (
            <div className="space-y-2 p-3 bg-blue-50 rounded-lg">
              <Label className="text-xs font-semibold text-blue-800">Payment Date Range (optional)</Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label className="text-[10px] text-gray-500">From</Label>
                  <Input type="date" value={dlPayDateStart} onChange={e => setDlPayDateStart(e.target.value)} data-testid="dl-pay-date-start" />
                </div>
                <div className="flex-1">
                  <Label className="text-[10px] text-gray-500">To</Label>
                  <Input type="date" value={dlPayDateEnd} onChange={e => setDlPayDateEnd(e.target.value)} data-testid="dl-pay-date-end" />
                </div>
              </div>
            </div>
          )}

          {/* Format Choice */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Format</Label>
            <div className="flex gap-2">
              <button onClick={() => setDlFormat('pdf')}
                className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${dlFormat === 'pdf' ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'} cursor-pointer`}
                data-testid="dl-format-pdf">
                <FileText className="w-4 h-4 text-red-600" /> <span className="font-medium text-sm">PDF</span>
              </button>
              <button onClick={() => setDlFormat('excel')}
                className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${dlFormat === 'excel' ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-gray-300'} cursor-pointer`}
                data-testid="dl-format-excel">
                <FileSpreadsheet className="w-4 h-4 text-green-600" /> <span className="font-medium text-sm">Excel</span>
              </button>
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-1">
            <Button variant="outline" onClick={() => setDownloadDialog(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 gap-1" onClick={executeDownload} data-testid="confirm-download-btn">
              <Download className="w-4 h-4" /> Download
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DownloadDialog;
